import {
  ADD_SCHEDULE_ACTIVITY_SUCCESS,
  ADD_SCHEDULE_ACTIVITY_ERROR,
  ADD_SCHEDULE_ACTIVITY_REQUEST,
  GET_SCHEDULE_ACTIVITIES_ERROR,
  GET_SCHEDULE_ACTIVITIES_REQUEST,
  GET_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_ERROR,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_REQUEST,
  UPDATE_DETAIL_ACTIVITY_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_ERROR,
  CLEAR_SCHEDULE_ACTIVITIES,
  GET_REGULARS_REQUEST,
  GET_REGULARS_SUCCESS,
  GET_REGULARS_ERROR,
  GET_REGULARS_CHUNK,
  SET_REGULARS_SCOPE,
  LOOK_AHEAD_SA_LOAD_FAILED,
  GET_REGULARS_FROM_CACHE,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_ERROR,
  UPDATE_REGULAR,
  DELETE_SCHEDULE_ACTIVITY,
  DELETE_SCHEDULE_ACTIVITIES,
  UPLOAD_MPP_REQUEST,
  UPLOAD_MPP_SUCCESS,
  UPLOAD_MPP_ERROR,
  UPDATE_SCHEDULE_ACTIVITIES_BY_IDS,
  RESET_BASELINE_ON_ACTIVITY,
  RESET_BASELINE_ON_SUMMARY,
  ADD_SUBTASK_SUCCESS,
  ADD_SUMMARY_SUCCESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_SUCCESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_ERROR,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_REQUEST,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_SUCCESS,
  GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_ERROR,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_REQUEST,
  ADD_SCHEDULE_ACTIVITIES,
  REALIGN_SCHEDULE_ACTIVITY_SEQUENCE,
  UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN,
  UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE,
  SHOW_MASK_ON_GANTT,
  UPDATE_FINISH_AND_START,
  PROPAGATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE,
  UPDATE_BATCH_OF_REGULARS,
  SET_RESOURCE_TASKS_ACTIVE,
  SET_BACKLOG_STATUSES,
} from "../types";
import produce from "immer";
import { saRowInWindow } from "../../utils/periodWindow/periodWindow";

/**
 * The schedule endpoints answer in two shapes: rows wrapped as
 * `{ scheduleActivity, hasConflicts, ...costs }`, or the activity flat. Only the
 * GET branch unwrapped them; the two "update" branches stored `payload` raw, so
 * against a wrapped response every item carried `id === undefined` and the
 * Gantt's id-based diff read the whole store as deleted.
 */
const normalizeScheduleActivities = (payload) =>
  (Array.isArray(payload) ? payload : []).map((row) => {
    if (!row || !row.scheduleActivity) return row;
    const { scheduleActivity, hasConflicts, ...rest } = row;
    return {
      ...scheduleActivity,
      globalCostAtCompletion: rest.globalCostAtCompletion || 0,
      laborCostAtCompletion: rest.laborCostAtCompletion || 0,
      plannedLaborCost: rest.plannedLaborCost || 0,
      plannedGlobalCost: rest.plannedGlobalCost || 0,
      laborCostActuals: rest.laborCostActuals || 0,
      laborCostRemaining: rest.laborCostRemaining || 0,
      globalCostActuals: rest.globalCostActuals || 0,
      globalCostRemaining: rest.globalCostRemaining || 0,
      // The duration axis' Actuals/Remaining are DTO-ROOT fields like the
      // labor/global cost pair above — `time_metrics.duration_actuals` is a
      // dead audit column, and the live values are derived per read by
      // ExtendedSAwithConflictsDTO.setDurationMetrics. Omitted from this
      // flatten they never reached the store, so formatOneTask's
      // `task.durationActuals || 0` wrote 0 into every record whatever the
      // progress: Duration Actuals stayed 0 and Duration Remaining stayed 0
      // even on a 100%-future planning, and a Date-of-Review change moved
      // Work (nested under genericMetrics, so it survived) while Duration
      // did not budge.
      durationActuals: rest.durationActuals || 0,
      durationRemaining: rest.durationRemaining || 0,
      spectrum: rest.spectrum || null,
      mappingTag: rest.mappingTag || null,
      // Only when the backend sent it: isTaggableRecord falls back to the row
      // flags for payloads older than tagLevel, and a materialized null would
      // silently disable the whole Tag column against such a backend.
      ...("tagLevel" in rest ? { tagLevel: rest.tagLevel } : {}),
      // SPEC-0022: resourceId -> peak % (null = no capacity), a DTO-ROOT field
      // like the costs. Absent while the load option is off: nothing is
      // materialised then (the chips stay neutral).
      ...("loadBadges" in rest ? { loadBadges: rest.loadBadges } : {}),
      ...("stockBadges" in rest ? { stockBadges: rest.stockBadges } : {}),
      ...("loadStale" in rest ? { loadStale: rest.loadStale === true } : {}),
      headScopeNumber: rest.headScopeNumber ?? null,
      headScopeLabel: rest.headScopeNumber > 0 ? `#${rest.headScopeNumber}` : "",
      hasConflicts,
    };
  });

/**
 * Dimensions that live at the ROOT of ExtendedSAwithConflictsDTO, not on the
 * nested entity. A numeric column reads 0, not null, so they carry as numbers.
 */
const ROOT_NUMERIC_DIMENSIONS = [
  "durationActuals",
  "durationRemaining",
  "laborCostAtCompletion",
  "globalCostAtCompletion",
  "plannedLaborCost",
  "plannedGlobalCost",
  "laborCostActuals",
  "laborCostRemaining",
  "globalCostActuals",
  "globalCostRemaining",
];

/**
 * Flattens one row of a partial refresh onto the row already in the store, with
 * one rule: a key the payload does not carry keeps its current value.
 *
 * Two shapes reach here. A wrapped row carries the derived dimensions at its
 * root; a bare entity carries none of them, which is what the reset-baseline
 * endpoints used to answer — the store then replaced the row with one that had
 * no Duration Actuals/Remaining and no labor/global cost, and syncPlanningFields
 * wrote 0 into those cells (its `|| 0` fallback, and its `value != null` filter
 * does not stop a 0). Carrying instead of zeroing keeps a frontend deployed
 * ahead of its backend correct on that path too.
 */
const mergeRowIntoExisting = (raw, existing) => {
  const wrapped = raw && raw.scheduleActivity ? raw : null;
  const activity = wrapped ? wrapped.scheduleActivity : raw;
  const extras = wrapped || {};
  const carry = (key) =>
    key in extras ? extras[key] ?? null : existing?.[key] ?? null;
  const headScopeNumber =
    "headScopeNumber" in extras
      ? extras.headScopeNumber ?? null
      : existing?.headScopeNumber ?? null;
  const merged = {
    ...activity,
    mappingTag: carry("mappingTag"),
    tagLevel: carry("tagLevel"),
    spectrum: carry("spectrum"),
    headScopeNumber,
    headScopeLabel: headScopeNumber > 0 ? `#${headScopeNumber}` : "",
    hasConflicts:
      "hasConflicts" in extras ? extras.hasConflicts : existing?.hasConflicts,
  };
  // SPEC-0022 badges: the payload's when it carries them, else the store's.
  if ("loadBadges" in extras) merged.loadBadges = extras.loadBadges;
  else if (existing && "loadBadges" in existing) merged.loadBadges = existing.loadBadges;
  if ("stockBadges" in extras) merged.stockBadges = extras.stockBadges;
  else if (existing && "stockBadges" in existing) merged.stockBadges = existing.stockBadges;
  if ("loadStale" in extras) merged.loadStale = extras.loadStale === true;
  ROOT_NUMERIC_DIMENSIONS.forEach((key) => {
    merged[key] = (key in extras ? extras[key] : existing?.[key]) || 0;
  });
  return merged;
};

const rowId = (row) => row?.scheduleActivity?.id ?? row?.id;

// A task save answers its backlog row without the fields the look-ahead load computed
// (backlogStatus, projectId, readOnly, saParentName): they are kept, never erased by null.
const mergeRegular = (item, payload) => {
  const merged = { ...item };
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined) merged[key] = value;
  });
  return merged;
};

const initialState = {
  item: {},
  items: [],
  isPartialScheduleLoad: false,
  // Row id -> schedule sequence the last by-ids refresh for that row rode on.
  // Guards an edited row against a full payload that was requested before it.
  byIdsStamps: {},
  regulars: [],
  // Delta-band period cache: regularsPool holds EVERY loaded row (deduped by SA id) across
  // the covered date range; regulars is the pool filtered by the selected display window.
  // Narrowing the period is a pure local re-window (zero network); widening only fetches the
  // missing band(s). Coverage carries the filters key — filters change => full reload.
  regularsPool: [],
  regularsCoverage: null,
  // MLAP-FILTER DEC-5: { code, status } when the look-ahead SA load was refused/failed.
  regularsLoadError: null,
  // MLAP-2: rows with a null start/finish are excluded server-side and COUNTED here for the notice.
  regularsIncompleteDates: 0,
  regularsWindow: null,
  // Per-session cache of look-ahead rows keyed by period+filters, so switching back to an
  // already-loaded period serves from memory instead of refetching. regularsCacheKey is the
  // key currently shown.
  regularsCache: {},
  regularsCacheKey: null,
  customColumns: [],
  customColumnNames: [],
  showMaskOnGantt: false,
  isFetching: false,
  isFetchingRegulars: false,
  isUpdating: false,
  isFetchingColumns: false,
  isFetchingColumnNames: false,
  error: "",
};
// Any structural mutation of look-ahead rows must invalidate the per-session period
// cache, otherwise navigating away and back would serve a stale snapshot. Listed
// centrally (and applied by the wrapper below) so add / delete / edit / quick-assign /
// smart-assign / batch-assign can never leave a stale cached period. Custom-column/PK
// edits are intentionally excluded: they live in the separate `customColumns` slice and
// are merged onto rows at render time, so the cached `regulars` stay correct.
const REGULARS_CACHE_INVALIDATING_TYPES = new Set([
  ADD_SCHEDULE_ACTIVITY_SUCCESS,
  ADD_SCHEDULE_ACTIVITIES,
  ADD_SUBTASK_SUCCESS,
  ADD_SUMMARY_SUCCESS,
  DELETE_SCHEDULE_ACTIVITY,
  DELETE_SCHEDULE_ACTIVITIES,
  UPDATE_REGULAR,
  UPDATE_BATCH_OF_REGULARS,
  SET_RESOURCE_TASKS_ACTIVE,
  SET_BACKLOG_STATUSES,
  UPDATE_SCHEDULE_ACTIVITIES_BY_IDS,
  UPDATE_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_SUCCESS,
  UPDATE_FINISH_AND_START,
  UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS,
  RESET_BASELINE_ON_ACTIVITY,
  RESET_BASELINE_ON_SUMMARY,
  UPLOAD_MPP_SUCCESS,
]);

// Rows from the chunked/full look-ahead load may embed their SA custom-column values
// (`row.customColumns`, keyed `column_<columnNameId>` — same shape as the whole-interval
// endpoint's entries). Upsert them into state.customColumns so the grid paints values
// together with the rows instead of waiting for the separate custom-columns request.
function upsertEmbeddedSACustomColumns(draftState, rows) {
  if (!Array.isArray(rows)) return;
  const withCC = rows.filter((r) => r && r.customColumns);
  if (!withCC.length) return;
  const indexById = new Map(
    draftState.customColumns.map((c, i) => [c.id, i])
  );
  withCC.forEach((r) => {
    const saId = r.scheduleActivity.id;
    const existingIndex = indexById.get(saId);
    if (existingIndex != null) {
      draftState.customColumns[existingIndex] = {
        ...draftState.customColumns[existingIndex],
        ...r.customColumns,
      };
    } else {
      indexById.set(saId, draftState.customColumns.length);
      draftState.customColumns.push({ id: saId, ...r.customColumns });
    }
  });
}

// eslint-disable-next-line
/**
 * Rows the backend changed alongside a creation — the project finish milestone gaining
 * the new row as a predecessor — replace their stale copy so the link survives the next
 * dependency diff.
 */
function replaceItemsById(items, updatedItems) {
  if (!updatedItems?.length) return items;
  const updatedById = new Map(updatedItems.map((item) => [item.id, item]));
  return items.map((item) => updatedById.get(item.id) || item);
}

function baseScheduleActivityReducer(
  state = initialState,
  { type, payload, meta }
) {
  switch (type) {
    case ADD_SCHEDULE_ACTIVITY_REQUEST:
      return produce(state, (draftState) => {
        draftState.error = "";
      });
    case ADD_SCHEDULE_ACTIVITY_SUCCESS:
      const payloadData = payload.scheduleActivity;
      const itemToAdd =
        "mappingTag" in payloadData
          ? {
            ...payloadData.scheduleActivity,
            mappingTag: payloadData.mappingTag,
          }
          : payloadData;
      // The realignment is a network call and belongs to the action, not here: an
      // immer producer must stay pure, and firing it from inside one left it racing
      // the very state update it was meant to follow.
      return produce(state, (draftState) => {
        draftState.items = replaceItemsById(
          [...draftState.items, itemToAdd],
          payload.reorderData?.updatedItems
        );
      });
    case ADD_SCHEDULE_ACTIVITY_ERROR:
      return produce(state, (draftState) => {
        draftState.error = payload;
      });
    case ADD_SCHEDULE_ACTIVITIES:
      return produce(state, (draftState) => {
        const newItems = draftState.items.slice();
        newItems.push(...payload.created);
        draftState.items = replaceItemsById(newItems, payload.updated);
      });
    // The backend renumbers uxId/parentUxId scenario-wide on every add, move-to
    // and reorder, and the gesture's response only carries the rows it touched.
    // Without this the rest of the store kept the previous numbering while the
    // Bryntum records moved to the new one, and the next incremental pass
    // re-parented rows from ordinals belonging to a generation that no longer
    // existed. Ordinals and parentage only — every other field stays as read.
    case REALIGN_SCHEDULE_ACTIVITY_SEQUENCE:
      return produce(state, (draftState) => {
        const alignedById = new Map(payload.map((row) => [row.id, row]));
        draftState.items.forEach((item) => {
          const aligned = alignedById.get(item.id);
          if (!aligned) return;
          item.uxId = aligned.uxId;
          item.parentUxId = aligned.parentUxId;
          item.parentSaId = aligned.parentSaId;
        });
      });
    case UPDATE_DETAIL_ACTIVITY_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case UPDATE_DETAIL_ACTIVITY_SUCCESS:
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.id === payload.id) {
            return { ...item, ...payload };
          }
          return item;
        }),
        isUpdating: false,
      };
    case UPDATE_DETAIL_ACTIVITY_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: payload,
      };
    case UPDATE_SCHEDULE_ACTIVITIES_REQUEST:
      return produce(state, (draftState) => {
        draftState.error = "";
        draftState.isUpdating = true;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.items = normalizeScheduleActivities(payload);
        draftState.isUpdating = false;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_ERROR:
      return produce(state, (draftState) => {
        draftState.error = payload;
        draftState.isUpdating = false;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS:
      // Handle server response from "Update usages" - replace items with recalculated schedule
      return produce(state, (draftState) => {
        draftState.items = normalizeScheduleActivities(payload);
        draftState.isUpdating = false;
      });
    case UPLOAD_MPP_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
      });
    case UPLOAD_MPP_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.items = payload;
      });
    case UPLOAD_MPP_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_SUCCESS:
      return {
        ...state,
        isUpdating: false,
      };
    case UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: payload,
      };
    case GET_SCHEDULE_ACTIVITIES_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
        draftState.items = [];
        draftState.regulars = [];
        draftState.error = "";
        // A fresh load (scenario switch) starts from no protected rows.
        draftState.byIdsStamps = {};
      });
    case GET_SCHEDULE_ACTIVITIES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.showMaskOnGantt = false;
        // A full payload replaces the store wholesale, so one requested BEFORE
        // an edit used to put the pre-edit row back — the edit visually
        // reverted although the server had it, and only Refresh showed the
        // truth. Rows refreshed by a by-ids call that rode a sequence at least
        // as recent as this request keep their merged value; everything else
        // takes the payload. The load itself is never cancelled, so a chunked
        // initial load still completes.
        const stamps = draftState.byIdsStamps || {};
        const requestId = meta?.requestId;
        const previousById = new Map();
        if (requestId != null) {
          draftState.items.forEach((row) => {
            if (row?.id != null) previousById.set(row.id, row);
          });
        }
        draftState.items = normalizeScheduleActivities(payload).map((row) => {
          if (requestId == null || row?.id == null) return row;
          const stamp = stamps[row.id];
          if (stamp == null || stamp < requestId) return row;
          return previousById.get(row.id) ?? row;
        });
        // Stamps older than this request are now satisfied by the payload
        // itself; keeping them would protect rows that no longer need it.
        if (requestId != null) {
          Object.keys(stamps).forEach((id) => {
            if (stamps[id] < requestId) delete draftState.byIdsStamps[id];
          });
        }
        draftState.isPartialScheduleLoad = Boolean(meta?.isPartial);
      });
    case GET_SCHEDULE_ACTIVITIES_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
        // A failed reload must not leave the Gantt unmounted behind the mask.
        draftState.showMaskOnGantt = false;
      });
    case DELETE_SCHEDULE_ACTIVITY:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.filter(
          (item) => item.id !== payload
        );
      });
    case DELETE_SCHEDULE_ACTIVITIES:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.filter(
          (item) => !payload.includes(item.id)
        );
      });
    case GET_REGULARS_REQUEST:
      return {
        ...state,
        items: [],
        isFetchingRegulars: true,
        regulars: [],
        error: "",
      };
    case GET_REGULARS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.regulars = payload;
        draftState.isFetchingRegulars = false;
        upsertEmbeddedSACustomColumns(draftState, payload);
      });
    case LOOK_AHEAD_SA_LOAD_FAILED:
      // A refused or lost load: the refusal must be literally true (no rows shown) and the
      // delta-band coverage must NOT claim the window is loaded — otherwise the user's natural
      // retry (a period change) re-windows locally and renders the failed window as a silent
      // empty grid, the exact outcome DEC-5 forbids. The next load falls through to a full
      // reload, which clears the refusal only when a fresh attempt actually starts.
      return produce(state, (draftState) => {
        draftState.regularsLoadError = payload;
        draftState.regularsCoverage = null;
        draftState.regularsPool = [];
        draftState.regulars = [];
        draftState.regularsTotalRows = 0;
        draftState.isFetchingRegulars = false;
        draftState.regularsStreaming = false;
      });
    case SET_REGULARS_SCOPE:
      // Delta-band scope switch: set the display window (+ coverage), re-derive the shown
      // rows from the pool locally. Narrowing a period lands ONLY here — zero network.
      return produce(state, (draftState) => {
        draftState.regularsLoadError = null;
        // The incomplete-dates count is a property of the filter set, not of the window: a
        // null-date row matches no period. Keep it across local re-windows; reset on a fresh pool.
        if (payload.resetPool) draftState.regularsIncompleteDates = 0;
        draftState.regularsWindow = payload.window || null;
        draftState.regularsCoverage = payload.coverage || null;
        if (payload.resetPool) draftState.regularsPool = [];
        draftState.regulars = draftState.regularsWindow
          ? draftState.regularsPool.filter((r) =>
              saRowInWindow(r, draftState.regularsWindow)
            )
          : draftState.regularsPool;
        // A scope switch settles the streaming state on the CURRENT window: a pure narrow
        // never dispatches a completing chunk, so without this the chip/counters from the
        // previous (wider) load stayed frozen on screen.
        draftState.regularsStreaming = false;
        draftState.regularsTotalRows = draftState.regulars.length;
        draftState.isFetchingRegulars = false;
        draftState.error = "";
      });
    case GET_REGULARS_CHUNK:
      return produce(state, (draftState) => {
        draftState.error = "";
        if (!payload.band && typeof payload.incompleteDates === "number") draftState.regularsIncompleteDates = payload.incompleteDates;
        // Chunks feed the POOL (deduped by SA id); the displayed list is the pool filtered
        // by the selected window. Band chunks (widening) thus append beyond the old window
        // while carried-over rows stay untouched.
        const poolCountBefore = draftState.regularsPool.length;
        if (payload.replace) {
          draftState.regularsPool = payload.rows;
        } else if (payload.rows && payload.rows.length) {
          const seen = new Set(
            draftState.regularsPool.map((r) => r.scheduleActivity.id)
          );
          payload.rows.forEach((r) => {
            if (!seen.has(r.scheduleActivity.id)) {
              seen.add(r.scheduleActivity.id);
              draftState.regularsPool.push(r);
            }
          });
        }
        draftState.regulars = draftState.regularsWindow
          ? draftState.regularsPool.filter((r) =>
              saRowInWindow(r, draftState.regularsWindow)
            )
          : draftState.regularsPool;
        upsertEmbeddedSACustomColumns(draftState, payload.rows);
        // Clear the loading flag as soon as the first batch is in (not when the last chunk
        // lands), so the grid unmasks and shows rows progressively while the rest stream.
        // Stay "fetching" only while there is still nothing to show.
        draftState.isFetchingRegulars =
          !payload.complete && draftState.regulars.length === 0;
        // Streaming meta for the non-blocking "X / Y rows" chip. A band load's totalRows
        // only counts the band — offset by what was already pooled.
        draftState.regularsStreaming = !payload.complete;
        if (typeof payload.totalRows === "number") {
          draftState.regularsTotalRows = payload.band
            ? poolCountBefore + payload.totalRows
            : payload.totalRows;
        } else if (payload.complete) {
          draftState.regularsTotalRows = draftState.regulars.length;
        }
        if (payload.complete && payload.key) {
          draftState.regularsCache[payload.key] = draftState.regulars;
          draftState.regularsCacheKey = payload.key;
        }
      });
    case GET_REGULARS_FROM_CACHE:
      return produce(state, (draftState) => {
        draftState.regulars = draftState.regularsCache[payload.key] || [];
        draftState.regularsCacheKey = payload.key;
        draftState.isFetchingRegulars = false;
        draftState.error = "";
      });
    case GET_REGULARS_ERROR:
      return {
        ...state,
        isFetchingRegulars: false,
        error: payload,
      };
    case UPDATE_REGULAR:
      return {
        ...state,
        regulars: state.regulars.map((item) => {
          if (item.scheduleActivity.id === payload.scheduleActivity.id) {
            return mergeRegular(item, payload);
          }
          return item;
        }),
      };
    case SET_RESOURCE_TASKS_ACTIVE:
    case SET_BACKLOG_STATUSES: {
      // A backlog row's Status is computed by the server from its tasks. Activation and progress
      // writes answer it: only that field is set, the row keeps everything else it was loaded with.
      const statusByActivityId = new Map(
        payload
          .filter((answer) => answer.scheduleActivityId != null && answer.backlogStatus)
          .map((answer) => [answer.scheduleActivityId, answer.backlogStatus])
      );
      if (!statusByActivityId.size || !Array.isArray(state.regulars)) return state;
      let changed = false;
      const regulars = state.regulars.map((item) => {
        const backlogStatus = statusByActivityId.get(item.scheduleActivity.id);
        if (!backlogStatus || backlogStatus === item.backlogStatus) return item;
        changed = true;
        return { ...item, backlogStatus };
      });
      return changed ? { ...state, regulars } : state;
    }
    case UPDATE_BATCH_OF_REGULARS: {
      // Pre-build Map for O(1) lookups instead of .find() O(n) per item
      const regularsPayloadMap = new Map(payload.map((sa) => [sa.id, sa]));
      return {
        ...state,
        regulars: state.regulars.map(item => {
          const relatedItem = regularsPayloadMap.get(item.scheduleActivity.id);
          // Immutable replace: state produced by the immer paths (chunk load) is frozen —
          // assigning item.scheduleActivity in place threw "Cannot assign to read only
          // property" and broke the multi-select batch delete.
          if (relatedItem) return { ...item, scheduleActivity: relatedItem };
          return item;
        })
      };
    }
    case UPDATE_SCHEDULE_ACTIVITIES_BY_IDS:
      return produce(state, (draftState) => {
        // Stamp the rows this refresh touched with the sequence it rode, so a
        // full payload requested earlier cannot overwrite them.
        if (meta?.requestId != null) {
          if (!draftState.byIdsStamps) draftState.byIdsStamps = {};
          payload.forEach((item) => {
            const id = item?.scheduleActivity?.id;
            if (id != null) draftState.byIdsStamps[id] = meta.requestId;
          });
        }
        // Pre-build index map for O(1) lookups instead of .findIndex() O(n) per payload item
        const indexById = new Map();
        draftState.items.forEach((item, idx) => {
          indexById.set(item.id, idx);
          if (item?.scheduleActivity?.id) {
            indexById.set(item.scheduleActivity.id, idx);
          }
        });
        // GET_SCHEDULE_ACTIVITIES_SUCCESS flattens the DTO extras (mappingTag,
        // spectrum, head-scope, the cost and duration dimensions) onto the item;
        // a by-ids refresh must not undo that. Replacing the item with the bare
        // scheduleActivity dropped the tag from the store after every edit, so a
        // re-render from state showed the row untagged while the assignment was
        // still persisted.
        payload.forEach((item) => {
          const index = indexById.get(item.scheduleActivity.id);
          if (index == null) return;
          draftState.items[index] = mergeRowIntoExisting(
            item,
            draftState.items[index]
          );
        });
      });
    // A reset baseline answers the rows it rewrote, in the same wrapped shape as
    // a by-ids refresh, so it merges the same way: the payload wins on every key
    // it carries, the store keeps the rest. Matching on the nested id as well as
    // the flat one keeps both response shapes addressable.
    case RESET_BASELINE_ON_ACTIVITY:
      return produce(state, (draftState) => {
        const id = rowId(payload);
        if (id == null) return;
        draftState.items = draftState.items.map((item) =>
          item.id == id ? mergeRowIntoExisting(payload, item) : item
        );
      });
    case RESET_BASELINE_ON_SUMMARY:
      return produce(state, (draftState) => {
        const saMap = new Map();
        (Array.isArray(payload) ? payload : []).forEach((row) => {
          const id = rowId(row);
          if (id != null) saMap.set(id, row);
        });
        draftState.items = draftState.items.map((item) => {
          const updated = saMap.get(item.id);
          return updated ? mergeRowIntoExisting(updated, item) : item;
        });
      });
    case ADD_SUBTASK_SUCCESS:
      return produce(state, (draftState) => {
        const { child, parent } = payload;
        const itemsWithParentUpdated = draftState.items.map((item) =>
          item.id === parent.id ? parent : item
        );
        draftState.items = [...itemsWithParentUpdated, child];
      });
    case ADD_SUMMARY_SUCCESS:
      const { parent, addedRegulars, summary } = payload;
      const addedTasks = [...addedRegulars, summary];
      return produce(state, (draftState) => {
        const itemsWithParentUpdated = draftState.items.map((item) =>
          item.id === parent.id ? parent : item
        );
        draftState.items = [...itemsWithParentUpdated, ...addedTasks];
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = true;
        draftState.error = "";
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = false;
        draftState.customColumns = payload || [];
      });
    case UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN:
      return produce(state, (draftState) => {
        draftState.customColumns = draftState.customColumns.map((column) =>
          column.id === payload.scheduleActivityId
            ? { ...column, ...payload.data }
            : column
        );
      });
    case UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE:
      return produce(state, (draftState) => {
        let updated = false;
        const newCustomColumns = draftState.customColumns.map((column) => {
          if (column.id === payload.id) {
            updated = true;
            return {
              ...column,
              [payload.columnKey]: payload.value,
            };
          } else return column;
        });
        if (!updated) {
          newCustomColumns.push({
            id: payload.id,
            [payload.columnKey]: payload.value,
          });
        }
        draftState.customColumns = newCustomColumns;
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = false;
        draftState.error = payload;
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = true;
        draftState.error = "";
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = false;
        draftState.customColumnNames = Array.isArray(payload) ? payload : [];
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = false;
        draftState.error = payload;
      });
    case CLEAR_SCHEDULE_ACTIVITIES:
      return {
        ...state,
        items: [],
        tasks: [],
      };
    case UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS:
      return {
        ...state,
        regulars: state.regulars.map((item) => {
          if (item.scheduleActivity.id === payload.id) {
            return {
              ...item,
              scheduleActivity: payload,
            };
          }
          return item;
        }),
      };
    case SHOW_MASK_ON_GANTT:
      return produce(state, (draftState) => {
        draftState.showMaskOnGantt = true;
      });
    case UPDATE_FINISH_AND_START:
      return produce(state, (draftState) => {
        draftState.regulars = draftState.regulars.map((item) => {
          if (item.scheduleActivity.id === payload.id) {
            item.scheduleActivity.startDate = payload.startDate;
            item.scheduleActivity.finishDate = payload.finishDate;
          }
          return item;
        });
      });
    case PROPAGATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE:
      return produce(state, (draftState) => {
        const nonExistingIds = payload.ids.filter((idToUpdate) =>
          draftState.customColumns.every(({ id }) => id != idToUpdate)
        );
        const updatedCustomColumns = draftState.customColumns.slice();
        // condition to avoid going through the array (which is sometimes big) when not necessary
        if (nonExistingIds.length !== payload.ids.length) {
          updatedCustomColumns.forEach((customColumn) => {
            const shouldPropagate = payload.ids.some(
              (id) => id == customColumn.id
            );
            if (shouldPropagate)
              customColumn[payload.customColumnField] = payload.value;
          });
        }
        nonExistingIds.forEach((id) => {
          const customColumn = { id };
          customColumn[payload.customColumnField] = payload.value;
          updatedCustomColumns.push(customColumn);
        });
        draftState.customColumns = updatedCustomColumns;
      });
    default:
      return state;
  }
}

// eslint-disable-next-line
export default function scheduleActivityReducer(state = initialState, action = {}) {
  const next = baseScheduleActivityReducer(state, action);
  if (
    next !== state &&
    REGULARS_CACHE_INVALIDATING_TYPES.has(action.type) &&
    next.regularsCache &&
    Object.keys(next.regularsCache).length > 0
  ) {
    // Also drop the delta-band coverage and resync the pool to the DISPLAYED rows: the
    // mutation reducers only touch `regulars`, so the pool would otherwise go stale. With
    // coverage null the next period change is a full reload (correctness over reuse).
    return {
      ...next,
      regularsCache: {},
      regularsCacheKey: null,
      regularsCoverage: null,
      regularsPool: next.regulars,
    };
  }
  return next;
}
